import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo, faSquareCheck } from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import {
  AppointmentDate,
  SelectedSchedule,
} from '@/features/common-requests/call-arrangement-context'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'

export type CallArrangementFormProps = {
  availableDates: Array<AppointmentDate>
  selectedSchedule: SelectedSchedule | null
  immediateSlotAvailable: boolean

  onSelectSchedule: (selected: SelectedSchedule | null) => void
}

const DEFAULT_TIME_SLOTS_TO_SHOW = 10 // 5 rows

export default function CallArrangementForm({
  availableDates,
  selectedSchedule,
  immediateSlotAvailable,
  onSelectSchedule,
}: CallArrangementFormProps) {
  const defaultDay =
    availableDates.length > 0 && !immediateSlotAvailable
      ? availableDates[0].day
      : null

  const [timeSlotsToShow, setTimeSlotsToShow] = useState(
    DEFAULT_TIME_SLOTS_TO_SHOW,
  )
  const [selectedDay, setSelectedDay] = useState(
    selectedSchedule && selectedSchedule.type === 'scheduled'
      ? selectedSchedule.selected.day
      : defaultDay,
  )

  function onSelectDay(day: string | null) {
    if (selectedDay === day) return

    setSelectedDay(day)
    setTimeSlotsToShow(DEFAULT_TIME_SLOTS_TO_SHOW)
  }

  function renderSelectableTime() {
    if (!selectedDay) return null

    const selectedDate = availableDates.find((d) => d.day === selectedDay)!

    const availableSlots = selectedDate.availableTimes

    if (availableSlots.length === 0)
      return (
        <p className="text-gray-700">
          <FontAwesomeIcon icon={faCircleInfo} className="mr-0.5" /> No more
          slots available on {selectedDate.date}.
        </p>
      )

    return (
      <>
        <h3 className="mb-1 mt-4 font-medium text-gray-800">Pick a time</h3>
        <div className="grid grid-cols-2 gap-x-1.5 gap-y-1.5">
          {availableSlots.slice(0, timeSlotsToShow).map((slot) => (
            <button
              type="button"
              key={slot.millis}
              className={clsx(
                'relative col-span-1 py-2.5',
                'before:content[""] before:absolute before:left-0 before:top-0 before:z-[2] before:h-full before:w-full before:rounded-md before:transition-all hover:before:border-gray-400',
                selectedSchedule?.type === 'scheduled' &&
                  selectedSchedule.selected.millis === slot.millis
                  ? 'text-blue-800 before:border-2 before:border-blue-800 hover:before:!border-blue-800'
                  : 'text-gray-700 before:border before:border-gray-300 hover:text-gray-800',
              )}
              onClick={() =>
                onSelectSchedule({
                  type: 'scheduled',
                  selected: {
                    ...selectedDate,
                    ...slot,
                  },
                })
              }
            >
              {slot.time}
            </button>
          ))}

          {availableSlots.length > timeSlotsToShow && (
            <button
              type="button"
              className="col-span-2 rounded-md border border-gray-300 bg-white py-2.5 text-gray-700 transition-all hover:border-gray-400 hover:text-gray-800"
              onClick={() => setTimeSlotsToShow(availableSlots.length)}
            >
              Show more slots{' '}
              <FontAwesomeIcon icon={faChevronDown} className="ml-1" />
            </button>
          )}
        </div>
      </>
    )
  }

  return (
    <div>
      <p className="text-medium text-gray-600">
        Our doctors will call you from{' '}
        <span className="font-medium underline decoration-dotted underline-offset-2">
          +61 390 072 145
        </span>
        . We recommend saving this number in your contacts.
      </p>
      <div className="mt-6">
        <button
          type="button"
          className={clsx(
            'before:content[""] relative flex w-full items-center justify-between rounded-lg bg-white px-4 py-3.5 text-left transition-all',
            'before:absolute before:left-0 before:top-0 before:z-[2] before:h-full before:w-full before:rounded-lg before:transition-all enabled:hover:before:border-gray-400',
            'group disabled:bg-gray-300',
            selectedSchedule?.type === 'immediate'
              ? 'before:border-2 before:border-blue-800 hover:before:!border-blue-800'
              : 'before:border before:border-gray-300',
          )}
          onClick={() => {
            onSelectDay(null)
            onSelectSchedule({ type: 'immediate' })
          }}
          disabled={!immediateSlotAvailable}
        >
          <div className="flex flex-col items-start">
            <p className="text-gray-800 group-disabled:text-gray-700">
              As soon as possible
            </p>
            <p className="text-sm text-gray-600 group-disabled:text-gray-500">
              Our doctors will call you{' '}
              <span className="font-semibold text-gray-700 group-disabled:text-gray-600">
                within next 2 hours
              </span>
            </p>
          </div>
          <span className="block">
            <FontAwesomeIcon
              icon={faSquareCheck}
              className={clsx(
                selectedSchedule?.type === 'immediate'
                  ? 'text-blue-800'
                  : 'text-gray-300',
              )}
              size="lg"
            />
          </span>
        </button>
        {!immediateSlotAvailable && (
          <p className="mt-2.5 text-gray-700">
            <FontAwesomeIcon icon={faCircleInfo} className="mr-0.5" /> It is out
            of our operating hours. Please schedule an appointment below.
          </p>
        )}
      </div>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-3 text-gray-500">OR</span>
        </div>
      </div>

      <h2 className="text-lg font-medium text-gray-800">Later</h2>
      <p className="mb-2 mt-3 text-base text-gray-700">
        <FontAwesomeIcon icon={faCircleInfo} className="mr-1" /> Our doctors
        will call you{' '}
        <span className="font-semibold underline underline-offset-2">
          within 2 hours
        </span>{' '}
        of the selected time.
      </p>
      <div className="mt-4 grid grid-cols-3 gap-x-1.5">
        {availableDates.map(({ day, date }) => (
          <DayButton
            day={day}
            date={date}
            key={day}
            onClick={() => {
              onSelectDay(day)
              onSelectSchedule(null)
            }}
            selected={selectedDay === day}
          />
        ))}
      </div>
      <div className="mb-6 mt-4">{renderSelectableTime()}</div>
    </div>
  )
}

type DayButtonProps = {
  day: string
  date: string

  onClick: () => void
  selected: boolean
}

function DayButton({ day, date, onClick, selected }: DayButtonProps) {
  return (
    <button
      type="button"
      className={clsx(
        'before:content[""] relative col-span-1 rounded-lg py-3 transition-all',
        'before:absolute before:left-0 before:top-0 before:z-[2] before:h-full before:w-full before:rounded-lg before:transition-all hover:before:border-gray-400',
        selected
          ? 'before:border-2 before:border-blue-800 hover:before:!border-blue-800'
          : 'before:border before:border-gray-300',
      )}
      onClick={onClick}
    >
      <p
        className={clsx(
          'text-sm',
          selected ? 'text-blue-600' : 'text-gray-600',
        )}
      >
        {day}
      </p>
      <p
        className={clsx(
          'font-medium',
          selected ? 'text-blue-800' : 'text-gray-800',
        )}
      >
        {date}
      </p>
    </button>
  )
}
